import { Microscope, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { parsePredictions } from './helpers';

/**
 * Results Display Card
 */
export function ResultsCard({ loading, error, result }) {
  return (
    <Card className="min-h-[400px]">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Resultater</CardTitle>
      </CardHeader>
      <CardContent>
        {loading && <LoadingState />}
        {error && !loading && <ErrorState error={error} />}
        {result && !loading && <SuccessState result={result} />}
        {!loading && !error && !result && <EmptyState />}
      </CardContent>
    </Card>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-teal-200 rounded-full"></div>
        <div className="absolute top-0 left-0 w-16 h-16 border-4 border-teal-500 rounded-full border-t-transparent animate-spin"></div>
      </div>
      <p className="mt-4 text-slate-600">Analyserer planktonbilde...</p>
      <p className="text-sm text-slate-400 mt-1">Dette kan ta opptil 2 minutter</p>
    </div>
  );
}

function ErrorState({ error }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
      <p className="text-red-600 font-medium">Feil ved identifikasjon</p>
      <p className="text-sm text-slate-500 mt-1 text-center">{error}</p>
    </div>
  );
}

function SuccessState({ result }) {
  const predictions = parsePredictions(result.predictions);
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-green-600 mb-4">
        <CheckCircle className="w-5 h-5" />
        <span className="font-medium">Identifikasjon fullført!</span>
      </div>
      
      <div className="space-y-3">
        <h4 className="font-medium text-slate-900">Top 5 prediksjoner:</h4>
        {predictions.map((pred, index) => (
          <div key={index} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">
                {index + 1}. {pred.label || pred.class || pred.species || 'Ukjent'}
              </span>
              <Badge variant={index === 0 ? 'default' : 'outline'}>
                {((pred.probability || pred.confidence || pred.score || 0) * 100).toFixed(1)}%
              </Badge>
            </div>
            <Progress 
              value={(pred.probability || pred.confidence || pred.score || 0) * 100} 
              className="h-2"
            />
          </div>
        ))}
      </div>

      {result.predictions?.raw_response && (
        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
          <p className="text-xs text-slate-500 mb-1">Rå respons:</p>
          <pre className="text-xs overflow-auto max-h-32">
            {JSON.stringify(result.predictions, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-slate-400">
      <Microscope className="w-12 h-12 mb-3" />
      <p>Last opp et bilde for å starte</p>
    </div>
  );
}
